import { FC, ReactNode, createContext, useContext, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addAlert } from 'modules/_shared/redux/alertActions';
import alertBodyTypes from 'modules/_shared/components/Alert/alertBodyTypes';

import encumbrancesExcelModalSteps, {
  EncumbrancesExcelModalStep,
} from '../constants/encumbrancesExcelModalSteps';
import {
  INITIAL_ENCUMBRANCES_IMPORT_PROGRESS,
  encumbrancesExcelModalInitValue,
} from '../constants/encumbrancesExcelModalInitValue';
import { EncumbrancesExcelModalContextType } from '../types/EncumbrancesExcelModalContextType';
import {
  EncumbranceExcelPartnerRef,
  EncumbrancesExcelPreview,
} from '../types/EncumbrancesExcel';
import { buildEncumbrancesExcelPreview } from '../utils/buildEncumbrancesExcelPreview';
import { downloadEncumbrancesExcelTemplate } from '../utils/downloadEncumbrancesExcelTemplate';
import {
  buildPartnerByCifMap,
  collectCifsFromEncumbranceExcelRawRows,
  fetchExistingPartnersForEncumbrancesExcel,
} from '../utils/fetchExistingPartnersForEncumbrancesExcel';
import {
  buildEncumbrancesBulkPayload,
  isBlockingGroup,
} from '../utils/encumbrancesExcelImportHelpers';
import { importEncumbrancesInBatches } from '../utils/importEncumbrancesInBatches';
import { parseEncumbrancesExcel } from '../utils/parseEncumbrancesExcel';

const EncumbrancesExcelModalContext =
  createContext<EncumbrancesExcelModalContextType>(
    encumbrancesExcelModalInitValue
  );

export const EncumbrancesExcelModalProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const [step, setStep] = useState<EncumbrancesExcelModalStep>(
    encumbrancesExcelModalSteps.UPLOAD
  );
  const [excelFile, setExcelFile] = useState<File | undefined>();
  const [excelPreview, setExcelPreview] =
    useState<EncumbrancesExcelPreview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isParsingExcel, setIsParsingExcel] = useState(false);
  const [uploadInformationalWithErrors, setUploadInformationalWithErrors] =
    useState(false);
  const [importProgress, setImportProgress] = useState(
    INITIAL_ENCUMBRANCES_IMPORT_PROGRESS
  );

  const rawRowsRef = useRef<Record<string, unknown>[]>([]);
  const existingPartnersRef = useRef<EncumbranceExcelPartnerRef[]>([]);

  const isImporting = step === encumbrancesExcelModalSteps.IMPORTING;

  const handleCloseModal = () => {
    if (isImporting) {
      return;
    }

    dispatch(setModal(null));
  };

  const handleDownloadExcelTemplate = () => {
    downloadEncumbrancesExcelTemplate();
  };

  const handleBackToUpload = () => {
    setStep(encumbrancesExcelModalSteps.UPLOAD);
    setUploadInformationalWithErrors(false);
  };

  const handleToggleUploadInformationalWithErrors = () => {
    setUploadInformationalWithErrors((current) => !current);
  };

  const handleChangeExcelFile = async (selectedFile: File) => {
    if (!selectedFile || !actualSociety?._id) return;

    setExcelFile(selectedFile);
    setIsParsingExcel(true);
    setIsLoading(true);
    setUploadInformationalWithErrors(false);

    try {
      const parseResult = await parseEncumbrancesExcel(selectedFile);

      if (!parseResult.success) {
        errorSwal(t('EncumbrancesExcelError_SHEET_NOT_FOUND'));
        setExcelFile(undefined);
        return;
      }

      rawRowsRef.current = parseResult.rows;

      const cifsInExcel = collectCifsFromEncumbranceExcelRawRows(
        parseResult.rows
      );
      const existingPartners = await fetchExistingPartnersForEncumbrancesExcel(
        actualSociety._id,
        cifsInExcel
      );

      existingPartnersRef.current = existingPartners;

      const preview = await buildEncumbrancesExcelPreview({
        rawRows: parseResult.rows,
        existingPartners,
        societyId: actualSociety._id,
      });

      setExcelPreview(preview);
      setStep(encumbrancesExcelModalSteps.PREVIEW);
    } catch {
      errorSwal(t('EncumbrancesExcelParseError'));
      setExcelFile(undefined);
    } finally {
      setIsParsingExcel(false);
      setIsLoading(false);
    }
  };

  const handleImport = async () => {
    if (!actualSociety?._id || !excelPreview) {
      return;
    }

    const hasBlockingGroups = excelPreview.groups.some((group) =>
      isBlockingGroup(group, uploadInformationalWithErrors)
    );

    if (hasBlockingGroups) {
      return;
    }

    const partnerByCif = buildPartnerByCifMap(existingPartnersRef.current);
    const encumbrances = buildEncumbrancesBulkPayload({
      groups: excelPreview.groups,
      partnerByCif,
      uploadInformationalWithErrors,
    });

    if (!encumbrances.length) {
      errorSwal(t('EncumbrancesExcelNoEncumbrances'));
      return;
    }

    setIsLoading(true);
    setStep(encumbrancesExcelModalSteps.IMPORTING);
    setImportProgress({
      ...INITIAL_ENCUMBRANCES_IMPORT_PROGRESS,
      total: encumbrances.length,
    });

    try {
      const result = await importEncumbrancesInBatches({
        societyId: actualSociety._id,
        encumbrances,
        onProgress: setImportProgress,
      });

      if (result.failed > 0 && result.created === 0) {
        errorSwal(
          result.firstError
            ? `${t('EncumbrancesExcelImportError')}\n${result.firstError}`
            : t('EncumbrancesExcelImportError')
        );
        setStep(encumbrancesExcelModalSteps.PREVIEW);
        return;
      }

      if (result.failed > 0) {
        const partialMessage = t('EncumbrancesExcelImportPartialSuccess', {
          created: result.created,
          failed: result.failed,
        });

        errorSwal(
          result.firstError
            ? `${partialMessage}\n${result.firstError}`
            : partialMessage
        );
      } else {
        successSwal(
          t('EncumbrancesExcelImportSuccess', { count: result.created })
        );
      }

      dispatch(addAlert(alertBodyTypes.SOCIETY_UPDATED));
      dispatch(setModal(null));
    } catch {
      errorSwal(t('EncumbrancesExcelImportError'));
      setStep(encumbrancesExcelModalSteps.PREVIEW);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <EncumbrancesExcelModalContext.Provider
      value={{
        data: {
          step,
          excelPreview,
          excelFile,
          isLoading,
          isParsingExcel,
          uploadInformationalWithErrors,
          importProgress,
        },
        actions: {
          handleCloseModal,
          handleChangeExcelFile,
          handleDownloadExcelTemplate,
          handleBackToUpload,
          handleToggleUploadInformationalWithErrors,
          handleImport,
        },
      }}
    >
      {children}
    </EncumbrancesExcelModalContext.Provider>
  );
};

export const useEncumbrancesExcelModal = () =>
  useContext(EncumbrancesExcelModalContext);
